import React, { useEffect, useState } from 'react';
import { api } from '../../../api/client';
import { DEFAULT_BIO_PHOTO } from '../../../data/bio';

export function BioPanel() {
    const [left, setLeft] = useState('');
    const [right, setRight] = useState('');
    const [photo, setPhoto] = useState('');
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [status, setStatus] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        Promise.allSettled([
            api.content.get('bio_left'),
            api.content.get('bio_right'),
            api.content.get('bio_photo'),
        ]).then(([l, r, p]) => {
            if (l.status === 'fulfilled') setLeft(l.value.value ?? '');
            if (r.status === 'fulfilled') setRight(r.value.value ?? '');
            if (p.status === 'fulfilled') setPhoto(p.value.value ?? '');
        });
    }, []);

    const save = async () => {
        setBusy(true);
        setError(null);
        setStatus(null);
        try {
            await Promise.all([
                api.content.set('bio_left', left),
                api.content.set('bio_right', right),
                api.content.set('bio_photo', photo),
            ]);
            setStatus('Saved.');
            setTimeout(() => setStatus(null), 2000);
        } catch (e: any) {
            setError(e.message ?? 'Save failed');
        } finally {
            setBusy(false);
        }
    };

    const uploadPhoto = async (file: File) => {
        setUploading(true);
        setError(null);
        try {
            const { url } = await api.images.upload(file);
            setPhoto(url);
        } catch (e: any) {
            setError(e.message ?? 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="admin-form">
            <h2>Bio</h2>
            <label>
                <span>Left paragraph</span>
                <textarea rows={8} value={left} onChange={(e) => setLeft(e.target.value)} />
            </label>
            <label>
                <span>Right paragraph</span>
                <textarea rows={8} value={right} onChange={(e) => setRight(e.target.value)} />
            </label>
            <div className="admin-field">
                <span className="admin-field-label">
                    Profile photo <span className="admin-field-hint">(shown at the bottom of the About page)</span>
                </span>
                <div className="admin-image-grid">
                    <div className="admin-image-thumb">
                        <img src={photo || DEFAULT_BIO_PHOTO} alt="" />
                        {!photo && <span className="admin-image-badge">default</span>}
                        {photo && (
                            <div className="admin-image-actions">
                                <button
                                    type="button"
                                    className="admin-image-remove"
                                    onClick={() => setPhoto('')}
                                    aria-label="Reset to default"
                                    title="Reset to default"
                                >×</button>
                            </div>
                        )}
                    </div>
                    <label className="admin-image-upload">
                        <span>{uploading ? 'Uploading…' : photo ? '+ Replace' : '+ Upload'}</span>
                        <input
                            type="file"
                            accept="image/*"
                            disabled={uploading}
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadPhoto(f);
                                e.target.value = '';
                            }}
                        />
                    </label>
                </div>
            </div>
            {error && <div className="admin-error">{error}</div>}
            {status && <div className="admin-status">{status}</div>}
            <div className="admin-form-actions">
                <button className="admin-primary" onClick={save} disabled={busy || uploading}>
                    {busy ? 'Saving…' : 'Save bio'}
                </button>
            </div>
        </div>
    );
}
